import { WelletContext } from '@/context/wellet/welletContext';
import { conciergeStatus } from '@/data';
import { filtersFormat, getPaymentMethods } from '@/helpers/format';
import mergeWithDefaults from '@/helpers/localstorage';
import { useDebounce, useLocalStorage, useOrderBy, usePagination, useUser } from '@/hooks';
import useResetFiltersBeforeUnload from '@/hooks/useResetFiltersBeforeUnload';
import { downloadConcierges, getAllAffiliatesScreen } from '@/services';
import { defaultQueryOptions } from '@/utils/defaultQueryOptions';
import downloadFileFromResponse from '@/utils/downloadFile';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { Show } from '@/components';
import {
  Button,
  ButtonFilter,
  CardSummary,
  Input,
  Loading,
  Money,
  PageError,
  Toast,
} from '@/uiComponents/common';
import {
  AlertCircle,
  ArrowUpCircle,
  Award,
  Banknote,
  Building2,
  ChartNoAxesCombined,
  CheckCircle,
  ClockAlert,
  Globe,
  Handshake,
  SearchIcon,
  Send,
  Users,
  XCircle,
} from 'lucide-react';
import { hasAllowedRoles } from '@/helpers/roles';
import { Table, TableCell } from '../../../components';
import { formatDateTranslation } from '@/helpers/dates';
import AffiliateCardNew from '@/uiComponents/common/affiliateCard/affiliateCardNew';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getAffiliateType } from '@/helpers/concierge';
import MercadoPago from '@/uiComponents/icons/mercadoPago';
import { Stripe } from '@/uiComponents/icons';

const paymentMethodConfig = {
  MERCADOPAGO: {
    variant: 'outline',
    icon: <MercadoPago size='12px' />,
  },
  CASH: {
    variant: 'outline',
    icon: <Banknote className='tw-w-3 tw-h-3' />,
  },
  CERTIFICATE: {
    variant: 'outline',
    icon: <Award className='tw-w-3 tw-h-3' />,
  },
  WIRE_MEXICO: {
    variant: 'outline',
    icon: <Send className='tw-w-3 tw-h-3' />,
  },
  WIRE_INTERNATIONAL: {
    variant: 'outline',
    icon: <Globe className='tw-w-3 tw-h-3' />,
  },
  STRIPE: {
    variant: 'outline',
    icon: <Stripe size='12px' />,
  },
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'ACTIVE':
      return (
        <div className='tw-relative tw-flex tw-items-center tw-justify-center'>
          <div className='tw-h-2 tw-w-2 tw-bg-green-500 tw-rounded-full tw-animate-pulse'></div>
          <div className='tw-absolute tw-h-2 tw-w-2 tw-bg-green-500 tw-rounded-full tw-opacity-30 tw-animate-ping'></div>
        </div>
      );
    case 'INACTIVE':
      return <div className='tw-h-2 tw-w-2 tw-bg-red-500 tw-rounded-full'></div>;
    case 'PENDING_APPROVAL':
      return <div className='tw-h-2 tw-w-2 tw-bg-yellow-500 tw-rounded-full'></div>;
    case 'REJECTED':
      return <div className='tw-h-2 tw-w-2 tw-bg-red-500 tw-rounded-full'></div>;
    case 'DELETED':
      return <div className='tw-h-2 tw-w-2 tw-bg-red-500 tw-rounded-full'></div>;
    default:
      return null;
  }
};

const getStatusLabel = (status, t) => {
  switch (status) {
    case 'ACTIVE':
      return t('statusBadge.active');
    case 'INACTIVE':
      return t('statusBadge.inactive');
    case 'DELETED':
      return t('statusBadge.delete');
    case 'PENDING_APPROVAL':
      return t('statusBadge.pending');
    case 'REJECTED':
      return t('statusBadge.rejected');
    default:
      return t('statusBadge.inactive');
  }
};

const InternalStaffTab = () => {
  const welletContext = useContext(WelletContext);
  const location = useLocation();
  const { t: translate } = useTranslation();
  const { t, i18n } = useTranslation('translation', { keyPrefix: 'screens.concierges' });
  const paymentMethods = useSelector((state) => state.app.paymentMethods);
  const { roles } = useUser();
  const [filterLocalStorage, setFilterLocalStorage, removeFilterLocalStorage] = useLocalStorage(
    'filter-internal',
    {
      filterStatus: conciergeStatus(translate),
      filterPaymentMethods: getPaymentMethods(paymentMethods, translate, true),
      filterField: '',
      filterByUrl: null,
      page: 1,
      filter: [],
      orderBy: 'DESC',
      orderType: 'createdDate',
    },
  );
  const organization = useSelector((state) => state.app.currentOrganization);
  const selectedOrganization = useSelector((state) => state.app.selectedOrganization);
  const [value, setValue] = useState(filterLocalStorage.filterField);
  const language = useSelector((state) => state.app.language);

  const debouncedSearch = useDebounce(value, 500);

  const [filterStatus, setFilterStatus] = useState(filterLocalStorage.filterStatus ?? []);
  const [filter, setFilter] = useState(filterLocalStorage?.filter);
  const [filterPaymentMethods, setFilterPaymentMethods] = useState(
    mergeWithDefaults(
      filterLocalStorage.filterPaymentMethods,
      getPaymentMethods(paymentMethods, translate, true),
    ),
  );

  const { orderBy, orderType, handleColumnClick } = useOrderBy(
    filterLocalStorage?.orderBy ? filterLocalStorage?.orderBy : null,
    filterLocalStorage?.orderType ? filterLocalStorage?.orderType : null,
  );

  const { currentPage, totalItems, handlePageChange, setTotalAndReset } = usePagination(
    filterLocalStorage.page,
  );
  const navigate = useNavigate();

  let limit = 10;
  let skip = 10 * ((currentPage ? currentPage : 1) - 1);

  // Main API Query
  const sellersQuery = useQuery({
    queryKey: [
      'internal',
      selectedOrganization.id,
      debouncedSearch,
      skip,
      filter,
      filtersFormat(filterPaymentMethods),
      filtersFormat(filterStatus),
      orderBy ?? 'DESC',
      orderType ?? 'createdDate',
    ],
    queryFn: () =>
      getAllAffiliatesScreen(
        welletContext, // welletContext
        limit, // limit
        skip, // skip
        organization.marketId, // marketID
        organization.id, // Org ID
        debouncedSearch.trim(), //fieldFilter
        false, // includeInactive
        [], // kyc filter
        true,
        [],
        [],
        [], // filtersFormat(filterRP),
        filtersFormat(filterPaymentMethods),
        [], // filtersFormat(filterBoss),
        [], // filtersFormat(filterLoyalty),
        orderBy ?? 'DESC',
        orderType ?? 'createdDate',
        null,
        true, //Include sales category
        [], // filter?.length > 0
        //   ? filter
        //   : ['ACTIVE', 'INACTIVE', 'DELETED', 'MISSING_DOCUMENTATION', 'REJECTED'],
        'INTERNAL_STAFF',
        [], // filtersFormat(filterEvidences),
        filter?.length > 0
          ? filter
          : ['VERIFIED', 'NO_VERIFIED', 'INACTIVE', 'CONCIERGE_REQUEST_PROMOTION'],
        filtersFormat(filterStatus),
      ),
    onSuccess: (data) => {
      setTotalAndReset(data?.data?.users?.totalRegisters);
      setFilterLocalStorage({
        ...filterLocalStorage,
        filterStatus,
        // filterKYC,
        filterField: value,
        filterPaymentMethods,
        page: currentPage,
        orderBy: orderBy ?? 'DESC',
        orderType: orderType ?? 'createdDate',
      });
    },
    ...defaultQueryOptions,
    keepPreviousData: true,
  });

  // Query Mutation
  const downloadConciergeMutation = useMutation({
    mutationFn: () => {
      const response = downloadConcierges(welletContext, organization.marketId, {
        KYCFilter: null,
        fieldFilter: debouncedSearch.trim(),
        includeInactive: false,
        includeSalesCategory: true,
        isAdvantageNetwork: true,
        advantageNetworkStatus: null,
        statusFilters: [],
        userStatus: [],
        withdrawalMethod:
          filtersFormat(filterPaymentMethods)?.length === 0
            ? null
            : filtersFormat(filterPaymentMethods),
        RPIds: null, // RPIds: filtersFormat(filterRP)?.length === 0 ? null : filtersFormat(filterRP),
        bossIds: null, // bossIds: filtersFormat(filterBoss)?.length === 0 ? null : filtersFormat(filterBoss),
        salesCategoriesId: null, //   filtersFormat(filterLoyalty).length === 0 ? null : filtersFormat(filterLoyalty),
        workCredentialStatus: null, // workCredentialStatus: filtersFormat(filterEvidences),
        affiliateFilter:
          filter?.length > 0
            ? filter
            : ['VERIFIED', 'NO_VERIFIED', 'INACTIVE', 'CONCIERGE_REQUEST_PROMOTION'],
        affiliateType: 'INTERNAL_STAFF',
        activeStatus:
          filtersFormat(filterStatus)?.length === 0 ? null : filtersFormat(filterStatus),
      });

      return response;
    },
    onSuccess: (data) => downloadFileFromResponse(data, 'concierges.xlsx'),
    onError: (error) => console.error(error),
  });

  const navigateToProfile = (seller) => {
    navigate(`/internalStaff/${selectedOrganization.id}/${seller.sid}/profile?tab=RESERVATION`, {
      state: location.pathname + location.search,
    });
  };
  const handleFilter = (filter) => {
    setFilter(filter);
    handlePageChange(1);
    setFilterLocalStorage({ ...filterLocalStorage, filter, page: 1 });
  };

  const headerAdmin = [
    { title: t('table.startDate'), value: 'createdDate', filterable: true },
    { title: '', value: '', filterable: false },
    { title: translate('generic.internal_staff'), value: '', filterable: false },
    { title: t('table.verification'), value: '', filterable: false },
    { title: t('table.organization'), value: '', filterable: false },
    {
      title: t('table.allReservations'),
      value: 'allReservation',
      filterable: true,
      center: true,
    },
    { title: t('table.last30daysVolume'), value: 'income', filterable: true, center: true },
    {
      title: t('table.last30daysReservations'),
      value: 'reservation',
      filterable: true,
      center: true,
    },
  ];

  useEffect(() => {
    setFilterPaymentMethods(
      mergeWithDefaults(
        filterLocalStorage.filterPaymentMethods,
        getPaymentMethods(paymentMethods, translate, true),
      ),
    );
    setFilterStatus(
      mergeWithDefaults(filterLocalStorage?.filterStatus, conciergeStatus(translate)),
    );
  }, [i18n.language]);

  useResetFiltersBeforeUnload(removeFilterLocalStorage);

  const safeFormatPhone = (phone) => {
    try {
      return parsePhoneNumberFromString(phone || '')?.formatInternational() || null;
    } catch {
      return null;
    }
  };

  const calculateVerificationPercentage = (verifications) => {
    if (verifications.length === 0) return 0;
    const completed = verifications?.filter((v) => v.status === 'COMPLETED')?.length;
    return Math.round((completed / verifications?.length) * 100);
  };

  const getVerificationLabel = (type, t) => {
    const labels = {
      PHONE: t('verification.phone'),
      EMAIL: t('verification.email'),
      WITHDRAWAL_METHOD: t('verification.withdrawal'),
      KYC: t('verification.kyc'),
      WORK_CREDENTIALS: t('verification.workCredentials'),
    };
    return labels[type] || type;
  };

  const hasPendingReview = (verifications) => {
    return verifications.some((v) => v.status === 'SEND');
  };

  return (
    <>
      <Show>
        <Show.When isTrue={sellersQuery.isLoading}>
          <Loading />
        </Show.When>
        <Show.When isTrue={sellersQuery.isError}>
          <PageError />
        </Show.When>
        <Show.Else>
          <div className='row px-1 mt-3 '>
            <div className='col-auto mb-2'>
              <CardSummary
                title={t('cardFilter.total')}
                value={sellersQuery?.data?.data?.totalUserStatusCount}
                onClick={() => handleFilter([], null)}
                icon={<ChartNoAxesCombined className='tw-h-4 tw-w-4 mt-0 tw-text-gray-500' />}
                isActive={filter?.length === 0}
              />
            </div>
            <div className='col-auto mb-2'>
              <CardSummary
                title={t('tabs.verified')}
                className={'!tw-w-60'}
                value={sellersQuery?.data?.data?.verifiedCount}
                onClick={() => handleFilter(['VERIFIED'], null)}
                icon={<CheckCircle className='tw-h-4 tw-w-4 mt-0 tw-text-gray-500' />}
                isActive={filter?.length > 0 && filter[0] === 'VERIFIED'}
              />
            </div>
            <div className='col-auto mb-2'>
              <CardSummary
                title={t('tabs.noVerified')}
                onClick={() => handleFilter(['NO_VERIFIED'], null)}
                value={sellersQuery?.data?.data?.noVerifiedCount}
                icon={<ClockAlert className='tw-h-4 tw-w-4 mt-0 tw-text-gray-500' />}
                isActive={filter?.length > 0 && filter[0] === 'NO_VERIFIED'}
              />
            </div>
            <div className='col-auto mb-2'>
              <CardSummary
                title={t('tabs.approveReject')}
                className={'!tw-w-auto'}
                value={sellersQuery?.data?.data?.conciergePromotionCount}
                onClick={() => handleFilter(['CONCIERGE_REQUEST_PROMOTION'], null)}
                icon={<ArrowUpCircle className='tw-h-4 tw-w-4 ml-2 mt-0 tw-text-gray-500' />}
                isActive={filter?.length > 0 && filter[0] === 'CONCIERGE_REQUEST_PROMOTION'}
              />
            </div>
          </div>
          <div className='d-flex justify-content-center justify-content-sm-start my-3 align-items-center'>
            <div className='col col-sm-auto'>
              <Input
                icon={<SearchIcon color='currentColor' size='16px' />}
                type={'search'}
                name={'search'}
                value={value}
                onChangeValue={(e) => setValue(e.target.value)}
                placeholder={t('search')}
              />
            </div>
            <div className='row'>
              <div className='mb-1 pl-1 col-auto justify-content-center'>
                <ButtonFilter
                  isSearchable={false}
                  filterName={t('buttonsFilter.state')}
                  filterOptions={filterStatus}
                  callback={setFilterStatus}
                />
              </div>
              <div className='mb-1 pl-2 col-auto justify-content-center'>
                <ButtonFilter
                  isSearchable={false}
                  filterName={t('buttonsFilter.getaway')}
                  filterOptions={filterPaymentMethods}
                  callback={setFilterPaymentMethods}
                />
              </div>
            </div>
            {hasAllowedRoles(roles, ['superadministrator', 'marketadmin', 'excel-concierge']) ? (
              <div className='col d-flex justify-content-end'>
                <Button
                  className={'btn-wellet-secondary opacity justify-content-center'}
                  size={'small'}
                  text={t('download')}
                  width={'36px'}
                  disabled={downloadConciergeMutation.isLoading}
                  loading={downloadConciergeMutation.isLoading}
                  onClick={() => downloadConciergeMutation.mutate()}
                />
              </div>
            ) : null}
          </div>
          <Show>
            <Show.When isTrue={sellersQuery.isPreviousData}>
              <div className='my-4 py-4'>
                <Loading global={false} />
              </div>
            </Show.When>
            <Show.Else>
              <Table
                data={sellersQuery?.data?.data?.users?.items ?? []}
                hasPagination={false}
                totalP={totalItems}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                columns={headerAdmin}
                onColumnClick={handleColumnClick}
                activeColumn={orderType}
                sortOrder={orderBy}
                renderRow={(seller) => {
                  const verificationPercentage = calculateVerificationPercentage(
                    seller?.verificationUser,
                  );
                  const completedVerifications = seller?.verificationUser?.filter(
                    (v) => v.status === 'COMPLETED',
                  );
                  const pendingVerifications = seller?.verificationUser?.filter(
                    (v) => v.status === 'PENDING',
                  );
                  const sentVerifications = seller?.verificationUser?.filter(
                    (v) => v.status === 'SEND',
                  );
                  const needsReview = hasPendingReview(seller?.verificationUser);
                  const { date } = formatDateTranslation(seller?.createdDate, language, 'small');
                  const config =
                    paymentMethodConfig[seller?.userWithDrawal?.withdrawalMethod?.methodType];
                  return (
                    <>
                      <TableCell className={'sentences-capitalize'}>{date}</TableCell>
                      <TableCell>
                        {seller?.status ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className='tw-bg-transparent'>
                                {getStatusIcon(seller?.status)}
                              </TooltipTrigger>
                              <TooltipContent>{getStatusLabel(seller?.status, t)}</TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </TableCell>
                      <TableCell style={{ minWidth: '200px' }}>
                        <AffiliateCardNew
                          url={seller.profilePic}
                          name={seller?.isVerified ? seller.name : seller.email}
                          company={safeFormatPhone(seller?.phone)}
                          primaryClass=''
                          affiliateType={seller?.affiliateType}
                          email={seller?.isVerified ? seller?.email : null}
                          isVerified={seller?.isVerified}
                          status={seller?.status}
                          showAffiliateIcon={true}
                          showVerifiedIcon={false}
                          t={t}
                          phone={seller?.isVerified ? safeFormatPhone(seller?.phone) : null}
                        />
                      </TableCell>
                      <TableCell>
                        <div className='tw-flex tw-items-center tw-gap-2'>
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className='tw-w-full tw-text-left tw-bg-transparent'>
                                <div className='tw-flex tw-flex-col tw-gap-1'>
                                  <div className='tw-flex tw-items-center tw-gap-2'>
                                    <span className='tw-text-sm tw-font-medium'>
                                      {verificationPercentage}%
                                    </span>
                                    {needsReview && (
                                      <AlertCircle className='tw-h-4 tw-w-4 tw-text-amber-600 tw-flex-shrink-0' />
                                    )}
                                  </div>
                                  <div className='tw-w-16 tw-h-1.5 tw-bg-gray-100 tw-rounded-full tw-overflow-hidden'>
                                    <div
                                      className='tw-h-full tw-bg-green-500 tw-rounded-full tw-transition-all'
                                      style={{ width: `${verificationPercentage}%` }}
                                    ></div>
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className='tw-w-56'>
                                <div className='tw-text-xs tw-space-y-2'>
                                  <div className='tw--medium tw-mb-2'>
                                    {t('verification.label')}
                                  </div>
                                  {completedVerifications.length > 0 && (
                                    <div>
                                      <div className='tw-font-medium !tw-mb-1 d-flex tw-items-center tw-gap-1'>
                                        <CheckCircle className='tw-h-3 tw-w-3' />
                                        {t('completed')}
                                      </div>
                                      <ul className='tw-space-y-1'>
                                        {completedVerifications.map((item) => (
                                          <li key={item.type} className=''>
                                            • {getVerificationLabel(item.type, t)}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {sentVerifications.length > 0 && (
                                    <div>
                                      <div className='tw-font-medium !tw-mb-1 d-flex tw-items-center tw-gap-1'>
                                        <AlertCircle className='tw-h-3 tw-w-3' /> {t('sent')}
                                      </div>
                                      <ul className='tw-space-y-1'>
                                        {sentVerifications.map((item) => (
                                          <li key={item.type} className=''>
                                            • {getVerificationLabel(item.type, t)}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {pendingVerifications.length > 0 && (
                                    <div>
                                      <div className='tw-font-medium !tw-mb-1 d-flex tw-items-center tw-gap-1'>
                                        <XCircle className='tw-h-3 tw-w-3' /> {t('missing')}
                                      </div>
                                      <ul className='tw-space-y-1'>
                                        {pendingVerifications.map((item) => (
                                          <li key={item.type}>
                                            • {getVerificationLabel(item.type, t)}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </TableCell>

                      <TableCell>
                        <div className='tw-max-w-44 tw-text-ellipsis'>
                          <div className='fw-500 wrap-text d-flex tw-gap-2 tw-items-center'>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger className='tw-bg-transparent'>
                                  {seller?.affiliateType === 'CONCIERGE' ? (
                                    <Building2 className={'tw-h-4 tw-w-4'} />
                                  ) : seller?.affiliateType === 'INTERNAL_STAFF' ? (
                                    <Handshake className={'tw-h-4 tw-w-4'} />
                                  ) : (
                                    <Users className={'tw-h-4 tw-w-4'} />
                                  )}{' '}
                                </TooltipTrigger>
                                <TooltipContent>
                                  {getAffiliateType(seller?.affiliateType, translate)}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <div className='wrap-text'>{seller.workUnit}</div>
                          </div>
                          <div className='dark-grey wrap-text'>
                            {seller?.salesCategory?.schemeType === 'LOYALTY'
                              ? seller?.salesCategory?.name ?? '-'
                              : null}
                          </div>
                          <div className='d-flex tw-items-center tw-gap-3'>
                            <div>
                              <div className='dark-grey wrap-text'>
                                {seller?.salesCategory?.isCommissionDisabled
                                  ? t('commissionDisabled')
                                  : seller?.salesCategory
                                  ? t('commission', {
                                      commission:
                                        seller?.salesCategory?.commission +
                                          seller?.salesCategory?.commissionPlus ?? 0,
                                    })
                                  : null}
                              </div>
                            </div>
                            {seller?.userWithDrawal ? (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger className='tw-bg-transparent'>
                                    <Badge
                                      variant={config?.variant}
                                      className='tw-gap-1.5 tw-px-2.5 tw-py-1 tw-bg-white'
                                    >
                                      {config?.icon}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {translate(
                                      `paymentMethods.${seller?.userWithDrawal?.withdrawalMethod?.methodType}`,
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={'text-center'} style={{ width: 'auto' }}>
                        {seller.allReservations}
                      </TableCell>
                      <TableCell className={'text-center'} style={{ width: 'auto' }}>
                        <Money value={seller.income ?? 0} />
                      </TableCell>
                      <TableCell className={'text-center'} style={{ width: 'auto' }}>
                        {seller.reservation}
                      </TableCell>
                    </>
                  );
                }}
                onRowClick={(seller) => navigateToProfile(seller)}
              />
            </Show.Else>
          </Show>
        </Show.Else>
      </Show>

      <Show.When isTrue={downloadConciergeMutation?.isError}>
        <Toast
          message={
            downloadConciergeMutation?.error?.response?.status === 413
              ? 'La cantidad de registros a descargar ha superado el límite.'
              : `Ocurrio un error`
          }
          type='ERROR'
          onCallback={() => {
            downloadConciergeMutation.reset();
          }}
        />
      </Show.When>
    </>
  );
};

export default InternalStaffTab;
